import React, { useState, useMemo } from 'react';
import { DollarSign, FileText, Clock, CheckCircle, RefreshCw, Trash2, ExternalLink, ChevronDown, ChevronRight, Undo2 } from 'lucide-react';
import Button from '../../UI/Button';

const FeeInvoiceTable = ({
    fees,
    summary,
    combinedInvoices = [],
    onGenerateInvoice,
    generatingFeeInvoice,
    userType,
    onRedirect,
    onMarkAsPaid,
    onMarkAsUnpaid,
    onMarkCombinedAsPaid,
    onMarkCombinedAsUnpaid,
    onRegenerateLink,
    onDeleteInvoice,
    regeneratingInvoice,
    deletingInvoice
}) => {
    // Track expanded state for combined invoices (default: all expanded)
    const [expandedInvoices, setExpandedInvoices] = useState(() => {
        const initial = {};
        combinedInvoices.forEach(inv => {
            initial[inv.invoice_id] = true; // Default expanded
        });
        return initial;
    });

    // Get set of invoice IDs that are combined (to filter out individual fees)
    const combinedInvoiceIds = useMemo(() => {
        return new Set(combinedInvoices.map(inv => inv.invoice_id));
    }, [combinedInvoices]);

    // Filter out fees that are part of a combined invoice
    const individualFees = useMemo(() => {
        if (!fees) return [];
        return fees.filter(fee => !fee.is_combined);
    }, [fees]);

    const toggleExpanded = (invoiceId) => {
        setExpandedInvoices(prev => ({
            ...prev,
            [invoiceId]: !prev[invoiceId]
        }));
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'uninvoiced':
                return <Clock size={16} />;
            case 'invoiced_unpaid':
                return <FileText size={16} />;
            case 'paid':
                return <CheckCircle size={16} />;
            default:
                return <Clock size={16} />;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'uninvoiced':
                return 'Not Invoiced';
            case 'invoiced_unpaid':
                return 'Invoiced - Unpaid';
            case 'paid':
                return 'Paid';
            default:
                return 'Unknown';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'uninvoiced':
                return 'text-gray-600 dark:text-gray-400';
            case 'invoiced_unpaid':
                return 'text-orange-600 dark:text-orange-400';
            case 'paid':
                return 'text-green-600 dark:text-green-400';
            default:
                return 'text-gray-600 dark:text-gray-400';
        }
    };

    // Show nothing if no fees and no combined invoices
    if ((!fees || fees.length === 0) && combinedInvoices.length === 0) {
        return null;
    }

    return (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg shadow-sm p-4 mt-4 mb-4 fee-invoice-table">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                    <DollarSign size={20} className="mr-2" />
                    Fee Invoicing
                </h3>
                {summary && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        Total: ${summary.total_fees} | Uninvoiced: {summary.uninvoiced_count} (${summary.uninvoiced_amount})
                    </div>
                )}
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                            <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Fee Type</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Amount</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Status</th>
                            <th className="text-left py-2 px-3 font-medium text-gray-700 dark:text-gray-300">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {/* Combined Invoices */}
                        {combinedInvoices.map((invoice, invoiceIndex) => {
                            const isExpanded = expandedInvoices[invoice.invoice_id] ?? true;
                            return (
                                <React.Fragment key={`combined-${invoice.invoice_id}`}>
                                    {/* Combined Invoice Header Row */}
                                    <tr
                                        className="border-b border-gray-200 dark:border-gray-600 bg-blue-50 dark:bg-blue-900/20 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30"
                                        onClick={() => toggleExpanded(invoice.invoice_id)}
                                    >
                                        {/* Fee Type - Combined Invoice Label */}
                                        <td className="py-3 px-3">
                                            <div className="flex items-center font-medium text-gray-900 dark:text-white">
                                                {isExpanded ? (
                                                    <ChevronDown size={16} className="mr-2 text-blue-500" />
                                                ) : (
                                                    <ChevronRight size={16} className="mr-2 text-blue-500" />
                                                )}
                                                <span className="text-blue-600 dark:text-blue-400">
                                                    Combined Invoice ({invoice.invoice_number})
                                                </span>
                                                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                                    ({invoice.fee_items?.length || 0} items)
                                                </span>
                                            </div>
                                        </td>

                                        {/* Amount */}
                                        <td className="py-3 px-3">
                                            <div className="font-semibold text-gray-900 dark:text-white">
                                                ${parseFloat(invoice.total_amount).toFixed(2)}
                                            </div>
                                        </td>

                                        {/* Status */}
                                        <td className="py-3 px-3">
                                            <div className={`flex items-center ${getStatusColor(invoice.status)}`}>
                                                {getStatusIcon(invoice.status)}
                                                <span className="ml-2">{getStatusText(invoice.status)}</span>
                                            </div>
                                        </td>

                                        {/* Action */}
                                        <td className="py-3 px-3" onClick={(e) => e.stopPropagation()}>
                                            {userType === 'admin' && invoice.status === 'invoiced_unpaid' && (
                                                <div className="flex flex-wrap gap-1">
                                                    <Button
                                                        onClick={() => onMarkCombinedAsPaid && onMarkCombinedAsPaid(invoice)}
                                                        variant="secondary"
                                                        className="text-xs px-2 py-1"
                                                    >
                                                        Mark as Paid
                                                    </Button>
                                                    {invoice.checkout_url && (
                                                        <Button
                                                            onClick={() => window.open(invoice.checkout_url, '_blank')}
                                                            variant="secondary"
                                                            className="text-xs px-2 py-1"
                                                            title="View Payment Link"
                                                        >
                                                            <ExternalLink size={12} />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        onClick={() => onRegenerateLink && onRegenerateLink({ invoice_id: invoice.invoice_id })}
                                                        disabled={regeneratingInvoice === invoice.invoice_id}
                                                        variant="secondary"
                                                        className="text-xs px-2 py-1"
                                                        title="Regenerate Payment Link"
                                                    >
                                                        <RefreshCw size={12} className={regeneratingInvoice === invoice.invoice_id ? 'animate-spin' : ''} />
                                                    </Button>
                                                    <Button
                                                        onClick={() => onDeleteInvoice && onDeleteInvoice({ invoice_id: invoice.invoice_id })}
                                                        disabled={deletingInvoice === invoice.invoice_id}
                                                        variant="danger"
                                                        className="text-xs px-2 py-1"
                                                        title="Delete Invoice"
                                                    >
                                                        <Trash2 size={12} />
                                                    </Button>
                                                </div>
                                            )}
                                            {invoice.status === 'paid' && (
                                                <div className="flex items-center gap-2">
                                                    <div className="flex items-center text-xs text-green-600 dark:text-green-400">
                                                        <CheckCircle size={14} className="mr-1" />
                                                        Paid
                                                    </div>
                                                    {userType === 'admin' && (
                                                        <Button
                                                            onClick={() => onMarkCombinedAsUnpaid && onMarkCombinedAsUnpaid(invoice)}
                                                            variant="secondary"
                                                            className="text-xs px-2 py-1"
                                                            title="Mark as Unpaid"
                                                        >
                                                            <Undo2 size={12} />
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>

                                    {/* Expanded Fee Items */}
                                    {isExpanded && invoice.fee_items?.map((fee, feeIndex) => (
                                        <tr
                                            key={`combined-${invoice.invoice_id}-fee-${feeIndex}`}
                                            className="border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
                                        >
                                            {/* Fee Type - Indented */}
                                            <td className="py-2 px-3 pl-10">
                                                <div className="text-gray-700 dark:text-gray-300">
                                                    {fee.label}
                                                </div>
                                            </td>

                                            {/* Amount */}
                                            <td className="py-2 px-3">
                                                <div className="text-gray-700 dark:text-gray-300">
                                                    ${parseFloat(fee.amount).toFixed(2)}
                                                </div>
                                            </td>

                                            {/* Status - inherited from parent */}
                                            <td className="py-2 px-3">
                                                <div className={`flex items-center text-xs ${getStatusColor(fee.status)}`}>
                                                    {getStatusIcon(fee.status)}
                                                    <span className="ml-1">{getStatusText(fee.status)}</span>
                                                </div>
                                            </td>

                                            {/* Action - no actions on individual items in combined invoice */}
                                            <td className="py-2 px-3">
                                                <span className="text-xs text-gray-400">—</span>
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            );
                        })}

                        {/* Individual Fees (not part of combined invoice) */}
                        {individualFees.map((fee, index) => (
                            <tr
                                key={`${fee.source}-${fee.source_id}-${fee.fee_field}`}
                                className={`border-b border-gray-100 dark:border-gray-700 ${
                                    index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-25 dark:bg-gray-750'
                                }`}
                            >
                                {/* Fee Type */}
                                <td className="py-3 px-3">
                                    <div className="font-medium text-gray-900 dark:text-white">
                                        {fee.label}
                                    </div>
                                </td>

                                {/* Amount */}
                                <td className="py-3 px-3">
                                    <div className="font-semibold text-gray-900 dark:text-white">
                                        ${parseFloat(fee.amount).toFixed(2)}
                                    </div>
                                </td>

                                {/* Status */}
                                <td className="py-3 px-3">
                                    <div className={`flex items-center ${fee.payment_label?.buttons?.className || getStatusColor(fee.payment_label?.status || fee.status)}`}>
                                        <div className={fee.payment_label?.buttons?.className || getStatusColor(fee.payment_label?.status || fee.status)}>
                                            {getStatusIcon(fee.payment_label?.status || fee.status)}
                                        </div>
                                        <span className="ml-2">{getStatusText(fee.payment_label?.status || fee.status)}</span>
                                    </div>
                                </td>

                                {/* Action */}
                                <td className="py-3 px-3">
                                    {/* Admin Actions */}
                                    {userType === 'admin' && fee.payment_label?.status === 'uninvoiced' && fee.payment_label?.buttons?.type === 'fee_invoice' && (
                                        <Button
                                            onClick={() => onGenerateInvoice(fee)}
                                            disabled={generatingFeeInvoice === `${fee.source}-${fee.source_id}-${fee.fee_field}`}
                                            variant="primary"
                                            className="text-xs px-3 py-1"
                                        >
                                            {generatingFeeInvoice === `${fee.source}-${fee.source_id}-${fee.fee_field}` ? 'Generating...' : fee.payment_label.buttons.label}
                                        </Button>
                                    )}

                                    {/* Admin Actions - Invoiced but Unpaid - Show Mark as Paid + View Link + Regenerate/Delete */}
                                    {userType === 'admin' && fee.payment_label?.status === 'invoiced_unpaid' && fee.invoice_id && (
                                        <div className="flex flex-wrap gap-1">
                                            {fee.payment_label?.buttons?.type === 'fee_payment' && (
                                                <Button
                                                    onClick={() => onMarkAsPaid && onMarkAsPaid(fee)}
                                                    variant="secondary"
                                                    className="text-xs px-2 py-1"
                                                >
                                                    {fee.payment_label.buttons.label}
                                                </Button>
                                            )}
                                            {fee.checkout_url && (
                                                <Button
                                                    onClick={() => window.open(fee.checkout_url, '_blank')}
                                                    variant="secondary"
                                                    className="text-xs px-2 py-1"
                                                    title="View Payment Link"
                                                >
                                                    <ExternalLink size={12} />
                                                </Button>
                                            )}
                                            <Button
                                                onClick={() => onRegenerateLink && onRegenerateLink(fee)}
                                                disabled={regeneratingInvoice === fee.invoice_id}
                                                variant="secondary"
                                                className="text-xs px-2 py-1"
                                                title="Regenerate Payment Link"
                                            >
                                                <RefreshCw size={12} className={regeneratingInvoice === fee.invoice_id ? 'animate-spin' : ''} />
                                            </Button>
                                            <Button
                                                onClick={() => onDeleteInvoice && onDeleteInvoice(fee)}
                                                disabled={deletingInvoice === fee.invoice_id}
                                                variant="danger"
                                                className="text-xs px-2 py-1"
                                                title="Delete Invoice"
                                            >
                                                <Trash2 size={12} />
                                            </Button>
                                        </div>
                                    )}

                                    {/* Client Actions - Payment Link */}
                                    {userType === 'client' && fee.payment_label?.status === 'invoiced_unpaid' && fee.payment_label?.buttons?.type === 'redirect' && (
                                        <Button
                                            onClick={() => onRedirect(fee.payment_label.buttons.link, fee.payment_label.buttons.useWindow)}
                                            variant="primary"
                                            className={`text-xs px-3 py-1 ${fee.payment_label.buttons.className || ''}`}
                                            disabled={!fee.payment_label.buttons.link}
                                        >
                                            {fee.payment_label.buttons.label}
                                        </Button>
                                    )}

                                    {/* Status displays for both user types */}
                                    {fee.payment_label?.status === 'uninvoiced' && userType === 'client' && (
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {fee.payment_label.title}
                                        </div>
                                    )}

                                    {fee.payment_label?.status === 'paid' && (
                                        <div className="flex items-center gap-2">
                                            <div className="flex items-center text-xs text-green-600 dark:text-green-400">
                                                <CheckCircle size={14} className="mr-1" />
                                                Completed
                                            </div>
                                            {userType === 'admin' && fee.invoice_id && (
                                                <Button
                                                    onClick={() => onMarkAsUnpaid && onMarkAsUnpaid(fee)}
                                                    variant="secondary"
                                                    className="text-xs px-2 py-1"
                                                    title="Mark as Unpaid"
                                                >
                                                    <Undo2 size={12} />
                                                </Button>
                                            )}
                                        </div>
                                    )}

                                    {/* Show payment complete icon if specified */}
                                    {fee.payment_label?.buttons?.showPaymentCompleteIcon && (
                                        <div className="flex items-center text-xs text-green-600 dark:text-green-400">
                                            <CheckCircle size={14} className="mr-1" />
                                            Paid
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Summary Footer */}
            {summary && summary.uninvoiced_count > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                            {summary.uninvoiced_count} fee{summary.uninvoiced_count !== 1 ? 's' : ''} pending invoice generation
                        </span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                            Total Uninvoiced: ${summary.uninvoiced_amount.toFixed(2)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeeInvoiceTable;
